import { ExpandableSection, Flex, smBumps, Typo } from '@wrisk/ui-components'
import { isNil } from 'lodash-es'
import React, { FunctionComponent } from 'react'

import { getAdditionalProfiles, InsuredProfile, Quote } from '../../../domain'
import { useExpandableSectionConfig } from '../../../hooks/adjustments/expandableSection'
import { toAdditionalProfileProposalData } from '../../../hooks/adjustments/proposal/additionalProfile/data'
import {
  tDefaults,
  TKeyBuilder,
  useWriskTranslation,
} from '../../../infrastructure/internationalisation'
import { AdditionalProfileManagementConfig } from '../../../state/configuration'
import { getFormatter } from '../../formBuilder'

const AdditionalProfileInformation: FunctionComponent<{
  summary: AdditionalProfileManagementConfig
  profile: InsuredProfile
  tKey: TKeyBuilder
}> = ({ summary, profile, tKey }) => {
  const { t } = useWriskTranslation()

  const toData = toAdditionalProfileProposalData(profile)

  const [header, inputsToRender] = useExpandableSectionConfig({
    sectionId: summary.id,
    inputs: summary.inputs,
    tKey,
    toData,
  })

  const expandedComponents = inputsToRender
    .filter(({ adjustment }) => !isNil(toData(adjustment)))
    .map((input) => {
      const Formatter = getFormatter(input.type)

      return (
        <Flex
          key={input.name}
          px={smBumps}
          py={2}
          flexDirection='column'
          alignItems='flex-start'
        >
          <Typo typoSize='xs' color='bodySecondary' mb={1}>
            {t(tDefaults('product.headers', input.name))}
          </Typo>
          <Typo as='pre'>
            <Formatter input={input} value={toData(input.adjustment)} t={t} />
          </Typo>
        </Flex>
      )
    })

  return (
    <ExpandableSection reversed header={<Typo fontWeight='bold'>{header}</Typo>}>
      {expandedComponents}
    </ExpandableSection>
  )
}

export const AdditionalProfilesInformation: FunctionComponent<{
  config: AdditionalProfileManagementConfig
  quote: Quote
  tKey: TKeyBuilder
}> = ({ config, quote, tKey }) => {
  const additionalProfiles = getAdditionalProfiles(quote)

  const sections = additionalProfiles.map((it) => (
    <AdditionalProfileInformation
      key={it.profileCode}
      summary={config}
      profile={it}
      tKey={tKey}
    />
  ))

  return <>{sections}</>
}
